import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { rewardApi } from '../../api/reward.api';
import { DailyTrendItem } from '../../types/Reward.api.type';

type PeriodOption = '오늘' | '시간대별' | '7D' | '30D' | 'ALL';

const PERIOD_TABS: { value: PeriodOption; label: string }[] = [
  { value: '오늘', label: '오늘' },
  { value: '시간대별', label: '시간대별' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

/**
 * 조각 적립 추이 라인 차트
 */
export const PieceIssuanceChart = () => {
  const [period, setPeriod] = useState<PeriodOption>('오늘');

  const isHourly = period === '시간대별' || period === '오늘';
  const hourlyMode = period === '시간대별' ? 'ALL' : 'TODAY';

  const { data: pieceEconomy, isLoading: isLoadingHourly } = useQuery({
    queryKey: ['adminPieceEconomy', hourlyMode],
    queryFn: () => rewardApi.getPieceEconomy(hourlyMode).then(res => res.data.results),
    enabled: isHourly,
  });

  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminRewardStats', period],
    queryFn: () => rewardApi.getRewardStats(period as Exclude<PeriodOption, '시간대별'>).then(res => res.data.results),
    enabled: !isHourly,
  });

  const isLoading = isHourly ? isLoadingHourly : isLoadingStats;

  const chartData: DailyTrendItem[] = isHourly
    ? (pieceEconomy?.hourlyIssuanceTrend ?? [])
    : (stats?.piece.dailyTrend ?? []).map((item: DailyTrendItem) => ({
        ...item,
        date: item.date.slice(5), // MM-DD
      }));

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <h3 className="text-lg font-semibold text-[#030213] mb-4">조각 적립 추이</h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-[#6B7280]">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#030213]">조각 적립 추이</h3>
        <div className="flex gap-2">
          {PERIOD_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setPeriod(tab.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                period === tab.value
                  ? 'bg-[#F59E0B] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#E5E7EB]">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="date"
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#6B7280"
              style={{ fontSize: '12px' }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Line
              type="monotone"
              dataKey="amount"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={{ fill: '#F59E0B', r: 4 }}
              activeDot={{ r: 6 }}
              name="조각 적립량"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
